import { useEffect, useMemo, useRef, useState, type CSSProperties, type FormEvent, type ReactNode } from "react";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { errorToast } from "@telluscompany/design-system";

import { useLocalization } from "@i18n";
import { bignumberCardStyle, goBignumberCardStyle } from "@shared/utils";

import type {
  AdminStructureDamAnmClassification,
  AdminStructureDamAnmClassificationResponse,
} from "../../api/structures/structures.types";
import { useAdminStructureAnmClassificationQuery } from "../../api/structures/structures.queries";
import { usePatchAdminStructureAnmClassificationMutation } from "../../api/structures/structures.mutations";
import { STRUCTURE_ANM_CLASSIFICATION_FORM_ID } from "./structure-modal-form-ids";
import {
  CLASSIFICATION_TAB_ROOT,
  ctd,
  classificationTabIconSx,
} from "./classification-tab-dark-mode";

import {
  buildAnmSections,
  getAllAnmColumnKeys,
  getAllAnmGradeKeys,
} from "../../catalog/build-anm-sections";
import type {
  CardOption,
  ClassificationLevel,
  ColumnDef,
  GoClassificationLevel,
  GradeDef,
  SectionDef,
} from "../../catalog/anm-classification.catalog.types";

const ANM_I18N = {
  classification: {
    high: "admin.structures.anmClassification.classification.high",
    medium: "admin.structures.anmClassification.classification.medium",
    low: "admin.structures.anmClassification.classification.low",
  },
  label: {
    criBignumberTitle: "admin.structures.anmClassification.label.criBignumberTitle",
    dpaBignumberTitle: "admin.structures.anmClassification.label.dpaBignumberTitle",
    goBignumberTitle: "admin.structures.anmClassification.label.goBignumberTitle",
    referenceDocButton: "admin.structures.anmClassification.label.referenceDocButton",
    referenceDocPanel: "admin.structures.anmClassification.label.referenceDocPanel",
    close: "admin.structures.anmClassification.label.close",
    total: "admin.structures.anmClassification.label.total",
    classificationStatus: "admin.structures.anmClassification.label.classificationStatus",
    partial: "admin.structures.anmClassification.label.partial",
    points: "admin.structures.anmClassification.label.points",
    classification: "admin.structures.anmClassification.label.classification",
    criterion: "admin.structures.anmClassification.label.criterion",
    band: "admin.structures.anmClassification.label.band",
    riskCategory: "admin.structures.anmClassification.label.riskCategory",
    riskCategoryCri: "admin.structures.anmClassification.label.riskCategoryCri",
  },
  criPanel: {
    tableTitle: "admin.structures.anmClassification.criPanel.tableTitle",
    ctRow: "admin.structures.anmClassification.criPanel.ctRow",
    ecRow: "admin.structures.anmClassification.criPanel.ecRow",
    psRow: "admin.structures.anmClassification.criPanel.psRow",
    totalFormula: "admin.structures.anmClassification.criPanel.totalFormula",
    riskClassification: "admin.structures.anmClassification.criPanel.riskClassification",
    bandsTitle: "admin.structures.anmClassification.criPanel.bandsTitle",
    bandHigh: "admin.structures.anmClassification.criPanel.bandHigh",
    bandMedium: "admin.structures.anmClassification.criPanel.bandMedium",
    bandLow: "admin.structures.anmClassification.criPanel.bandLow",
    footnote: "admin.structures.anmClassification.criPanel.footnote",
  },
  dpaPanel: {
    tableTitle: "admin.structures.anmClassification.dpaPanel.tableTitle",
    damageCategoryDpa: "admin.structures.anmClassification.dpaPanel.damageCategoryDpa",
    volumeRow: "admin.structures.anmClassification.dpaPanel.volumeRow",
    populationRow: "admin.structures.anmClassification.dpaPanel.populationRow",
    environmentalRow: "admin.structures.anmClassification.dpaPanel.environmentalRow",
    socioeconomicRow: "admin.structures.anmClassification.dpaPanel.socioeconomicRow",
    totalLabel: "admin.structures.anmClassification.dpaPanel.totalLabel",
    damageClassification: "admin.structures.anmClassification.dpaPanel.damageClassification",
    bandsTitle: "admin.structures.anmClassification.dpaPanel.bandsTitle",
    damageCategory: "admin.structures.anmClassification.dpaPanel.damageCategory",
    bandHigh: "admin.structures.anmClassification.dpaPanel.bandHigh",
    bandMedium: "admin.structures.anmClassification.dpaPanel.bandMedium",
    bandLow: "admin.structures.anmClassification.dpaPanel.bandLow",
    matrixFooter: "admin.structures.anmClassification.dpaPanel.matrixFooter",
  },
  goClassification: {
    aa: "admin.structures.anmClassification.goClassification.aa",
    a: "admin.structures.anmClassification.goClassification.a",
    b: "admin.structures.anmClassification.goClassification.b",
    c: "admin.structures.anmClassification.goClassification.c",
    d: "admin.structures.anmClassification.goClassification.d",
  },
  goPanel: {
    tableTitle: "admin.structures.anmClassification.goPanel.tableTitle",
    operationalCategory: "admin.structures.anmClassification.goPanel.operationalCategory",
    risrRow: "admin.structures.anmClassification.goPanel.risrRow",
    rpsbRow: "admin.structures.anmClassification.goPanel.rpsbRow",
    conformityRow: "admin.structures.anmClassification.goPanel.conformityRow",
    inspectionRow: "admin.structures.anmClassification.goPanel.inspectionRow",
    edrRow: "admin.structures.anmClassification.goPanel.edrRow",
    pgrbmRow: "admin.structures.anmClassification.goPanel.pgrbmRow",
    certificationRow: "admin.structures.anmClassification.goPanel.certificationRow",
    totalLabel: "admin.structures.anmClassification.goPanel.totalLabel",
    operationalClassification: "admin.structures.anmClassification.goPanel.operationalClassification",
    bandsTitle: "admin.structures.anmClassification.goPanel.bandsTitle",
    gopCategory: "admin.structures.anmClassification.goPanel.gopCategory",
    bandAa: "admin.structures.anmClassification.goPanel.bandAa",
    bandA: "admin.structures.anmClassification.goPanel.bandA",
    bandB: "admin.structures.anmClassification.goPanel.bandB",
    bandC: "admin.structures.anmClassification.goPanel.bandC",
    bandD: "admin.structures.anmClassification.goPanel.bandD",
    footnote: "admin.structures.anmClassification.goPanel.footnote",
  },
} as const;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export type AdminStructureAnmClassificationExternalClassification = {
  data: AdminStructureDamAnmClassificationResponse | null | undefined;
  isLoading?: boolean;
};

export type AdminStructureAnmClassificationTabProps = {
  complexId?: string;
  structureId: string;
  canWrite: boolean;
  onSaved?: (classification: AdminStructureDamAnmClassification) => void;
  onSavingChange?: (isSaving: boolean) => void;
  /** General read-only: inject GET payload and skip admin API. */
  externalClassification?: AdminStructureAnmClassificationExternalClassification;
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type Selections = Record<string, number | undefined>;

const dataToSelections = (
  data: AdminStructureDamAnmClassification,
  sections: SectionDef[],
): Selections => {
  const s: Selections = {};
  sections.forEach((section) => {
    section.grades.forEach((grade) => {
      grade.columns.forEach((col) => {
        const gradeKey = grade.key as keyof AdminStructureDamAnmClassification;
        const colLetter = col.letter;
        const gradeData = data[gradeKey] as Record<string, number> | undefined;
        s[col.key] = gradeData?.[colLetter];
      });
    });
  });
  return s;
};

const selectionsToPayload = (
  s: Selections,
  sections: SectionDef[],
): AdminStructureDamAnmClassification => {
  const result: Record<string, Record<string, number>> = {};
  sections.forEach((section) => {
    section.grades.forEach((grade) => {
      if (!result[grade.key]) result[grade.key] = {};
      grade.columns.forEach((col) => {
        result[grade.key][col.letter] = s[col.key]!;
      });
    });
  });
  return result as unknown as AdminStructureDamAnmClassification;
};

const sumGrade = (s: Selections, columns: ColumnDef[]): number =>
  columns.reduce((acc, col) => acc + (s[col.key] ?? 0), 0);

const criClassification = (total: number, hasEcScore10: boolean): ClassificationLevel => {
  if (total >= 80 || hasEcScore10) return "high";
  if (total > 40) return "medium";
  return "low";
};

const dpaClassification = (total: number): ClassificationLevel => {
  if (total >= 13) return "high";
  if (total > 7) return "medium";
  return "low";
};

/** ANEXO I p. 33 — resolucao-anm.pdf; 60 → D (classe mais severa). */
const goClassification = (total: number): GoClassificationLevel => {
  if (total === 0) return "aa";
  if (total <= 7) return "a";
  if (total <= 35) return "b";
  if (total <= 59) return "c";
  return "d";
};

const classificationLabel = (
  level: ClassificationLevel,
  translate: (key: string) => string,
): string => translate(ANM_I18N.classification[level]);

const goClassificationLabel = (
  level: GoClassificationLevel,
  translate: (key: string) => string,
): string => translate(ANM_I18N.goClassification[level]);

const formatCardScore = (score: number): string => score.toString().padStart(2, "0");

/** Máximo de colunas visíveis sem scroll; grades maiores usam essa largura por coluna e rolam. */
const MATRIX_MAX_VISIBLE_COLUMNS = 5;
const MATRIX_COLUMN_GAP_PX = 8;
const calcMatrixColumnWidth = (containerWidth: number, columnCount: number): number => {
  if (containerWidth <= 0) return 0;

  const fiveColumnWidth =
    (containerWidth - (MATRIX_MAX_VISIBLE_COLUMNS - 1) * MATRIX_COLUMN_GAP_PX) /
    MATRIX_MAX_VISIBLE_COLUMNS;

  if (columnCount <= MATRIX_MAX_VISIBLE_COLUMNS) {
    return (containerWidth - (columnCount - 1) * MATRIX_COLUMN_GAP_PX) / columnCount;
  }

  return fiveColumnWidth;
};

const useMatrixColumnWidth = (columnCount: number, enabled: boolean) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [columnWidthPx, setColumnWidthPx] = useState(0);

  useEffect(() => {
    if (!enabled) return;

    const el = scrollRef.current;
    if (!el) return;

    const measure = () => {
      setColumnWidthPx(calcMatrixColumnWidth(el.clientWidth, columnCount));
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [columnCount, enabled]);

  return {
    scrollRef,
    columnWidthPx,
    needsScroll: columnCount > MATRIX_MAX_VISIBLE_COLUMNS,
  };
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

type OptionCardProps = {
  colKey: string;
  option: CardOption;
  selected: boolean;
  disabled: boolean;
  onChange: (score: number) => void;
};

const OptionCard = ({ colKey, option, selected, disabled, onChange }: OptionCardProps) => (
  <button
    type="button"
    role="radio"
    aria-label={`${colKey} ${option.score}`}
    aria-checked={selected}
    disabled={disabled}
    onClick={() => onChange(option.score)}
    className={[
      "box-border flex h-full min-h-[128px] w-full flex-col items-center justify-between rounded-xl border p-2.5 text-center transition-colors",
      selected
        ? "border-[#1e170d] bg-[#ab9068] text-white"
        : `border-[#d9d9d9] bg-white text-[#4d4d4d] hover:border-[#ab9068]/60 ${ctd.optionIdle}`,
      disabled ? "cursor-default opacity-70" : "cursor-pointer",
    ].join(" ")}
  >
    <span className="flex w-full flex-1 items-center justify-center px-1 text-sm leading-snug">
      {option.text}
    </span>
    <span className={["mt-1 shrink-0 text-base font-bold", selected ? "text-white" : `text-[#4d4d4d] ${ctd.optionScore}`].join(" ")}>
      {formatCardScore(option.score)}
    </span>
  </button>
);

const ColumnHeaderCell = ({ column }: { column: ColumnDef }) => (
  <div className={`box-border flex h-[76px] min-h-[76px] max-h-[76px] items-center justify-center overflow-hidden rounded-xl border border-[#d9d9d9] bg-[#f2f2f2] p-2.5 text-center ${ctd.surfaceMuted}`}>
    <p className={`line-clamp-3 w-full text-sm font-bold leading-normal text-[#4d4d4d] ${ctd.text}`}>
      {column.title} ({column.letter})
    </p>
  </div>
);

type GradeOptionMatrixProps = {
  grade: GradeDef;
  selections: Selections;
  disabled: boolean;
  columnWidthPx: number;
  needsScroll: boolean;
  onSelect: (key: string, score: number) => void;
};

const GradeOptionMatrix = ({
  grade,
  selections,
  disabled,
  columnWidthPx,
  needsScroll,
  onSelect,
}: GradeOptionMatrixProps) => {
  const columnCount = grade.columns.length;
  const rowCount = Math.max(...grade.columns.map((col) => col.options.length), 0);

  const gridStyle: CSSProperties = {
    gridTemplateColumns:
      columnWidthPx > 0
        ? `repeat(${columnCount}, ${columnWidthPx}px)`
        : `repeat(${columnCount}, minmax(0, 1fr))`,
    ...(needsScroll && columnWidthPx > 0
      ? {
          width:
            columnCount * columnWidthPx + (columnCount - 1) * MATRIX_COLUMN_GAP_PX,
        }
      : { width: "100%" }),
  };

  return (
    <div className="grid gap-2" style={gridStyle}>
      {grade.columns.map((col) => (
        <ColumnHeaderCell key={`header-${col.key}`} column={col} />
      ))}
      {Array.from({ length: rowCount }, (_, rowIndex) =>
        grade.columns.map((col) => {
          const option = col.options[rowIndex];
          const cellKey = `${col.key}-row-${rowIndex}`;

          if (!option) {
            return <div key={cellKey} aria-hidden className="min-h-0" />;
          }

          return (
            <div key={cellKey} role="radiogroup" aria-label={col.key} className="flex min-h-[128px]">
              <OptionCard
                colKey={col.key}
                option={option}
                selected={selections[col.key] === option.score}
                disabled={disabled}
                onChange={(score) => onSelect(col.key, score)}
              />
            </div>
          );
        }),
      )}
    </div>
  );
};

type SectionBignumberProps = {
  title: string;
  total: number;
  classificationLabel: string;
  classificationLevel?: ClassificationLevel;
  goLevel?: GoClassificationLevel;
  referenceDocAriaLabel: string;
  totalAriaLabel: string;
  classificationAriaLabel: string;
  infoOpen: boolean;
  onToggleInfo: () => void;
};

const SectionBignumber = ({
  title,
  total,
  classificationLabel,
  classificationLevel,
  goLevel,
  referenceDocAriaLabel,
  totalAriaLabel,
  classificationAriaLabel,
  infoOpen,
  onToggleInfo,
}: SectionBignumberProps) => {
  const style =
    goLevel !== undefined
      ? goBignumberCardStyle(goLevel)
      : bignumberCardStyle(classificationLevel ?? "low");

  return (
    <div
      className={[
        "w-full max-w-[344px] rounded-2xl border px-6 py-4 shadow-[1px_2px_4px_rgba(25,25,28,0.08)] transition-colors",
        style.card,
      ].join(" ")}
    >
      <div className="flex items-center justify-between gap-2">
        <p className={["text-lg font-bold leading-snug", style.title].join(" ")}>{title}</p>
        <button
          type="button"
          aria-label={referenceDocAriaLabel}
          aria-expanded={infoOpen}
          onClick={onToggleInfo}
          className={[
            "flex h-12 w-12 shrink-0 cursor-pointer items-center justify-center rounded-lg hover:bg-black/5",
            style.title,
            ctd.hoverSubtle,
          ].join(" ")}
        >
          <InfoOutlinedIcon sx={classificationTabIconSx} />
        </button>
      </div>
      <p aria-label={totalAriaLabel} className={["mt-2 text-[40px] font-bold leading-normal", style.score].join(" ")}>
        {String(total)}
      </p>
      <span
        role="status"
        aria-label={classificationAriaLabel}
        className={["mt-1 block text-base font-medium", style.label].join(" ")}
      >
        {classificationLabel}
      </span>
    </div>
  );
};

type DocPanelCellProps = {
  children: ReactNode;
  header?: boolean;
  narrow?: boolean;
  wide?: boolean;
  align?: "left" | "center" | "right";
};

const DocPanelCell = ({
  children,
  header = false,
  narrow = false,
  wide = false,
  align = "center",
}: DocPanelCellProps) => (
  <div
    className={[
      "flex items-center border border-[#d9d9d9] px-2 py-1 text-sm",
      ctd.border,
      header ? `bg-[#f2f2f2] font-bold text-[#4d4d4d] ${ctd.surfaceMuted} ${ctd.text}` : `bg-white font-normal text-[#4d4d4d] ${ctd.surfaceWhite} ${ctd.text}`,
      narrow ? "w-[65px] shrink-0 justify-center" : wide ? "min-w-0 flex-1" : "w-[275px] shrink-0 justify-center",
      align === "right" ? "justify-end text-right" : align === "left" ? "justify-start text-left" : "justify-center text-center",
    ].join(" ")}
  >
    {children}
  </div>
);

type DocPanelTableProps = {
  panelAriaLabel: string;
  panelTitle: string;
  closeLabel: string;
  onClose: () => void;
  children: ReactNode;
};

const DocPanelTable = ({ panelAriaLabel, panelTitle, closeLabel, onClose, children }: DocPanelTableProps) => (
  <div
    role="region"
    aria-label={panelAriaLabel}
    className={`overflow-hidden rounded-lg border border-[#d9dee5] bg-[#fafafc] ${ctd.docPanel}`}
  >
    <div className={`flex h-11 items-center justify-between bg-[#f0f2f7] px-4 ${ctd.docHeader}`}>
      <p className={`text-sm font-semibold text-[#26262e] ${ctd.text}`}>{panelTitle}</p>
      <button type="button" onClick={onClose} className="cursor-pointer text-[13px] text-[#3366cc]">
        {closeLabel}
      </button>
    </div>
    <div className={`flex flex-col gap-2 bg-white p-0 ${ctd.docBody}`}>{children}</div>
  </div>
);

type GradeAccordionProps = {
  grade: GradeDef;
  partialValue: number;
  partialAriaLabel: string;
  expanded: boolean;
  disabled: boolean;
  selections: Selections;
  onToggle: () => void;
  onSelect: (key: string, score: number) => void;
};

const GradeAccordion = ({
  grade,
  partialValue,
  partialAriaLabel,
  expanded,
  disabled,
  selections,
  onToggle,
  onSelect,
}: GradeAccordionProps) => {
  const columnCount = grade.columns.length;
  const { scrollRef, columnWidthPx, needsScroll } = useMatrixColumnWidth(columnCount, expanded);

  return (
    <div className="flex w-full flex-col">
      <button
        type="button"
        onClick={onToggle}
        className={`flex h-10 w-full cursor-pointer items-center justify-between rounded-t-xl border border-[#d9d9d9] bg-white px-4 py-2 shadow-[1px_2px_4px_rgba(25,25,28,0.08)] ${ctd.surfaceWhite} ${ctd.text}`}
      >
        <span className={`flex items-center gap-2 text-[#4d4d4d] ${ctd.iconHost}`}>
          <CheckCircleOutlineIcon sx={classificationTabIconSx} />
          <span className="text-sm font-semibold">{grade.fullTitle}</span>
        </span>
        {expanded ? (
          <KeyboardArrowUpIcon sx={classificationTabIconSx} />
        ) : (
          <KeyboardArrowDownIcon sx={classificationTabIconSx} />
        )}
      </button>

      {expanded ? (
        <div className={`flex flex-col gap-4 rounded-b-2xl border border-t-0 border-[#d9d9d9] px-4 pb-4 pt-2 shadow-[1px_2px_8px_rgba(25,25,28,0.08)] ${ctd.gradeBody}`}>
          <input
            type="text"
            aria-label={partialAriaLabel}
            value={String(partialValue)}
            readOnly
            className={`h-10 w-24 rounded-xl border border-[#999] bg-white px-4 text-sm text-[#4d4d4d] ${ctd.surfaceWhite} ${ctd.text}`}
          />
          <div
            ref={scrollRef}
            className={needsScroll ? "overflow-x-auto pb-2" : "overflow-x-hidden pb-2"}
          >
            <GradeOptionMatrix
              grade={grade}
              selections={selections}
              disabled={disabled}
              columnWidthPx={columnWidthPx}
              needsScroll={needsScroll}
              onSelect={onSelect}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const AdminStructureAnmClassificationTab = ({
  complexId,
  structureId,
  canWrite,
  onSaved,
  onSavingChange,
  externalClassification,
}: AdminStructureAnmClassificationTabProps) => {
  const { translate } = useLocalization();
  const useExternalClassification = externalClassification !== undefined;
  const { data: adminData } = useAdminStructureAnmClassificationQuery(
    complexId ?? "",
    structureId,
    { enabled: !useExternalClassification && Boolean(complexId && structureId) },
  );
  const data = useExternalClassification ? externalClassification.data : adminData;
  const mutation = usePatchAdminStructureAnmClassificationMutation();

  useEffect(() => {
    return () => {
      onSavingChange?.(false);
    };
  }, [onSavingChange]);

  const anmSections = useMemo(() => buildAnmSections(translate), [translate]);

  const [selections, setSelections] = useState<Selections>({});
  const [openPanels, setOpenPanels] = useState<Record<string, boolean>>({});
  const [openGrades, setOpenGrades] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(getAllAnmGradeKeys().map((key) => [key, true])),
  );

  useEffect(() => {
    if (data === undefined) return;
    setSelections(data === null ? {} : dataToSelections(data, anmSections));
  }, [data, anmSections]);

  const criSection = anmSections[0]!;
  const dpaSection = anmSections[1]!;
  const goSection = anmSections[2]!;

  const ctGrade = criSection.grades[0]!;
  const ecGrade = criSection.grades[1]!;
  const psbGrade = criSection.grades[2]!;
  const dpaGrade = dpaSection.grades[0]!;
  const goGrade = goSection.grades[0]!;

  const ctPartial = useMemo(() => sumGrade(selections, ctGrade.columns), [selections, ctGrade.columns]);
  const ecPartial = useMemo(() => sumGrade(selections, ecGrade.columns), [selections, ecGrade.columns]);
  const psbPartial = useMemo(() => sumGrade(selections, psbGrade.columns), [selections, psbGrade.columns]);
  const dpaPartial = useMemo(() => sumGrade(selections, dpaGrade.columns), [selections, dpaGrade.columns]);
  const goPartial = useMemo(() => sumGrade(selections, goGrade.columns), [selections, goGrade.columns]);

  const criTotal = ctPartial + ecPartial + psbPartial;
  const dpaTotal = dpaPartial;
  const goTotal = goPartial;

  const hasEcScore10 = ecGrade.columns.some((col) => selections[col.key] === 10);
  const criClassLevel = criClassification(criTotal, hasEcScore10);
  const dpaClassLevel = dpaClassification(dpaTotal);
  const goClassLevel = goClassification(goTotal);
  const criClassLabel = classificationLabel(criClassLevel, translate);
  const dpaClassLabel = classificationLabel(dpaClassLevel, translate);
  const goClassLabel = goClassificationLabel(goClassLevel, translate);

  const handleSelect = (key: string, score: number) => {
    setSelections((prev) => ({ ...prev, [key]: score }));
  };

  const togglePanel = (key: string) => {
    setOpenPanels((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleGrade = (key: string) => {
    setOpenGrades((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const isComplete = getAllAnmColumnKeys().every((k) => selections[k] !== undefined);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (useExternalClassification || !canWrite || !complexId) {
      return;
    }
    if (!isComplete) {
      errorToast({
        title: translate("admin.structures.anmClassification.validation.incompleteSelectionTitle"),
        description: translate(
          "admin.structures.anmClassification.validation.incompleteSelectionDescription",
        ),
      });
      return;
    }
    const payload = selectionsToPayload(selections, anmSections);
    onSavingChange?.(true);
    try {
      const saved = await mutation.mutateAsync({ complexId, structureId, payload });
      onSaved?.(saved);
    } finally {
      onSavingChange?.(false);
    }
  };

  // Após a 1ª inspeção finalizada o Estado de conservação passa a ser mantido
  // pela ficha de inspeção (inspection-form.md §4.1) — só a grade EC trava.
  const isEcLocked = data?.ecReadOnly === true;

  const renderGrades = (grades: GradeDef[], partials: Record<string, number>) => (
    <div className="flex flex-col gap-4">
      {grades.map((grade) => (
        <GradeAccordion
          key={grade.key}
          grade={grade}
          partialValue={partials[grade.key] ?? 0}
          partialAriaLabel={translate(ANM_I18N.label.partial, { grade: grade.code })}
          expanded={openGrades[grade.key] ?? true}
          disabled={!canWrite || (grade.key === ecGrade.key && isEcLocked)}
          selections={selections}
          onToggle={() => toggleGrade(grade.key)}
          onSelect={handleSelect}
        />
      ))}
    </div>
  );

  const formatPanelPoints = (value: number | undefined) =>
    value === undefined ? "00" : String(value);

  return (
    <form
      id={STRUCTURE_ANM_CLASSIFICATION_FORM_ID}
      noValidate
      onSubmit={(e) => { void handleSubmit(e); }}
      className={`${CLASSIFICATION_TAB_ROOT} flex flex-col gap-10`}
    >
      {/* CRI */}
      <section className="flex flex-col gap-4">
        <p className="text-base font-normal text-foreground">{criSection.subtitle}</p>
        <h3 className="sr-only">{criSection.label}</h3>

        <SectionBignumber
          title={translate(ANM_I18N.label.criBignumberTitle)}
          total={criTotal}
          classificationLabel={criClassLabel}
          classificationLevel={criClassLevel}
          referenceDocAriaLabel={translate(ANM_I18N.label.referenceDocButton, { section: criSection.label })}
          totalAriaLabel={translate(ANM_I18N.label.total, { section: criSection.label })}
          classificationAriaLabel={translate(ANM_I18N.label.classificationStatus, {
            section: criSection.label,
          })}
          infoOpen={!!openPanels["cri"]}
          onToggleInfo={() => togglePanel("cri")}
        />

        {openPanels["cri"] ? (
          <DocPanelTable
            panelAriaLabel={translate(ANM_I18N.label.referenceDocPanel, { section: criSection.label })}
            panelTitle={translate(ANM_I18N.label.referenceDocPanel, { section: criSection.label })}
            closeLabel={translate(ANM_I18N.label.close)}
            onClose={() => togglePanel("cri")}
          >
            <div className="p-2">
              <div className={`rounded-xl border border-[#d9d9d9] bg-[#f2f2f2] px-2 py-1 text-center text-sm font-bold text-[#4d4d4d] ${ctd.surfaceMuted} ${ctd.text}`}>
                {translate(ANM_I18N.criPanel.tableTitle)}
              </div>

              <div className={`mt-2 overflow-hidden rounded-xl border border-[#d9d9d9] ${ctd.border}`}>
                <div className="flex">
                  <DocPanelCell header narrow>1</DocPanelCell>
                  <DocPanelCell header wide>{translate(ANM_I18N.label.riskCategoryCri)}</DocPanelCell>
                  <DocPanelCell header>{translate(ANM_I18N.label.points)}</DocPanelCell>
                </div>
                <div className="flex">
                  <DocPanelCell header narrow>1.1</DocPanelCell>
                  <DocPanelCell wide align="center">{translate(ANM_I18N.criPanel.ctRow)}</DocPanelCell>
                  <DocPanelCell>{formatPanelPoints(ctPartial)}</DocPanelCell>
                </div>
                <div className="flex">
                  <DocPanelCell header narrow>1.2</DocPanelCell>
                  <DocPanelCell wide align="center">{translate(ANM_I18N.criPanel.ecRow)}</DocPanelCell>
                  <DocPanelCell>{formatPanelPoints(ecPartial)}</DocPanelCell>
                </div>
                <div className="flex">
                  <DocPanelCell header narrow>1.3</DocPanelCell>
                  <DocPanelCell wide align="center">{translate(ANM_I18N.criPanel.psRow)}</DocPanelCell>
                  <DocPanelCell>{formatPanelPoints(psbPartial)}</DocPanelCell>
                </div>
                <div className="flex">
                  <DocPanelCell wide align="right">
                    <span className="font-bold">{translate(ANM_I18N.criPanel.totalFormula)}</span>
                  </DocPanelCell>
                  <DocPanelCell>
                    <span className="font-bold">{formatPanelPoints(criTotal)}</span>
                  </DocPanelCell>
                </div>
                <div className="flex">
                  <DocPanelCell wide align="right">
                    <span className="font-bold">{translate(ANM_I18N.criPanel.riskClassification)}</span>
                  </DocPanelCell>
                  <DocPanelCell>
                    <span className="font-bold">{criClassLabel}</span>
                  </DocPanelCell>
                </div>
              </div>

              <div className={`mt-2 overflow-hidden rounded-xl border border-[#d9d9d9] ${ctd.border}`}>
                <div className="flex">
                  <DocPanelCell header wide>{translate(ANM_I18N.criPanel.bandsTitle)}</DocPanelCell>
                  <div className="flex min-w-0 flex-1 flex-col">
                    <div className="flex">
                      <DocPanelCell header wide>{translate(ANM_I18N.label.riskCategory)}</DocPanelCell>
                      <DocPanelCell header wide>{criSection.label}</DocPanelCell>
                    </div>
                    <div className="flex">
                      <DocPanelCell wide>{translate(ANM_I18N.classification.high)}</DocPanelCell>
                      <DocPanelCell wide>{translate(ANM_I18N.criPanel.bandHigh)}</DocPanelCell>
                    </div>
                    <div className="flex">
                      <DocPanelCell wide>{translate(ANM_I18N.classification.medium)}</DocPanelCell>
                      <DocPanelCell wide>{translate(ANM_I18N.criPanel.bandMedium)}</DocPanelCell>
                    </div>
                    <div className="flex">
                      <DocPanelCell wide>{translate(ANM_I18N.classification.low)}</DocPanelCell>
                      <DocPanelCell wide>{translate(ANM_I18N.criPanel.bandLow)}</DocPanelCell>
                    </div>
                  </div>
                </div>
                <div className={`border border-[#d9d9d9] bg-[#f2f2f2] px-2 py-1 text-xs font-medium text-[#4d4d4d] ${ctd.surfaceMuted} ${ctd.text}`}>
                  {translate(ANM_I18N.criPanel.footnote)}
                </div>
              </div>
            </div>
          </DocPanelTable>
        ) : null}

        {renderGrades([ctGrade, ecGrade, psbGrade], {
          ct: ctPartial,
          ec: ecPartial,
          psb: psbPartial,
        })}
      </section>

      {/* DPA */}
      <section className="flex flex-col gap-4">
        <p className="text-base font-normal text-foreground">{dpaSection.subtitle}</p>
        <h3 className="sr-only">{dpaSection.label}</h3>

        <SectionBignumber
          title={translate(ANM_I18N.label.dpaBignumberTitle)}
          total={dpaTotal}
          classificationLabel={dpaClassLabel}
          classificationLevel={dpaClassLevel}
          referenceDocAriaLabel={translate(ANM_I18N.label.referenceDocButton, { section: dpaSection.label })}
          totalAriaLabel={translate(ANM_I18N.label.total, { section: dpaSection.label })}
          classificationAriaLabel={translate(ANM_I18N.label.classificationStatus, {
            section: dpaSection.label,
          })}
          infoOpen={!!openPanels["dpa"]}
          onToggleInfo={() => togglePanel("dpa")}
        />

        {openPanels["dpa"] ? (
          <DocPanelTable
            panelAriaLabel={translate(ANM_I18N.label.referenceDocPanel, { section: dpaSection.label })}
            panelTitle={translate(ANM_I18N.label.referenceDocPanel, { section: dpaSection.label })}
            closeLabel={translate(ANM_I18N.label.close)}
            onClose={() => togglePanel("dpa")}
          >
            <div className="p-2">
              <div className={`rounded-xl border border-[#d9d9d9] bg-[#f2f2f2] px-2 py-1 text-center text-sm font-bold text-[#4d4d4d] ${ctd.surfaceMuted} ${ctd.text}`}>
                {translate(ANM_I18N.dpaPanel.tableTitle)}
              </div>

              <div className={`mt-2 overflow-hidden rounded-xl border border-[#d9d9d9] ${ctd.border}`}>
                <div className="flex">
                  <DocPanelCell header narrow>2</DocPanelCell>
                  <DocPanelCell header wide>{translate(ANM_I18N.dpaPanel.damageCategoryDpa)}</DocPanelCell>
                  <DocPanelCell header>{translate(ANM_I18N.label.points)}</DocPanelCell>
                </div>
                <div className="flex">
                  <DocPanelCell header narrow>2.1</DocPanelCell>
                  <DocPanelCell wide align="center">{translate(ANM_I18N.dpaPanel.volumeRow)}</DocPanelCell>
                  <DocPanelCell>{formatPanelPoints(selections["dpa-a"])}</DocPanelCell>
                </div>
                <div className="flex">
                  <DocPanelCell header narrow>2.2</DocPanelCell>
                  <DocPanelCell wide align="center">{translate(ANM_I18N.dpaPanel.populationRow)}</DocPanelCell>
                  <DocPanelCell>{formatPanelPoints(selections["dpa-b"])}</DocPanelCell>
                </div>
                <div className="flex">
                  <DocPanelCell header narrow>2.3</DocPanelCell>
                  <DocPanelCell wide align="center">{translate(ANM_I18N.dpaPanel.environmentalRow)}</DocPanelCell>
                  <DocPanelCell>{formatPanelPoints(selections["dpa-c"])}</DocPanelCell>
                </div>
                <div className="flex">
                  <DocPanelCell header narrow>2.4</DocPanelCell>
                  <DocPanelCell wide align="center">{translate(ANM_I18N.dpaPanel.socioeconomicRow)}</DocPanelCell>
                  <DocPanelCell>{formatPanelPoints(selections["dpa-d"])}</DocPanelCell>
                </div>
                <div className="flex">
                  <DocPanelCell wide align="right">
                    <span className="font-bold">{translate(ANM_I18N.dpaPanel.totalLabel)}</span>
                  </DocPanelCell>
                  <DocPanelCell>
                    <span className="font-bold">{formatPanelPoints(dpaTotal)}</span>
                  </DocPanelCell>
                </div>
                <div className="flex">
                  <DocPanelCell wide align="right">
                    <span className="font-bold">{translate(ANM_I18N.dpaPanel.damageClassification)}</span>
                  </DocPanelCell>
                  <DocPanelCell>
                    <span className="font-bold">{dpaClassLabel}</span>
                  </DocPanelCell>
                </div>
              </div>

              <div className={`mt-2 overflow-hidden rounded-xl border border-[#d9d9d9] ${ctd.border}`}>
                <div className="flex">
                  <DocPanelCell header wide>{translate(ANM_I18N.dpaPanel.bandsTitle)}</DocPanelCell>
                  <div className="flex min-w-0 flex-1 flex-col">
                    <div className="flex">
                      <DocPanelCell header wide>{translate(ANM_I18N.dpaPanel.damageCategory)}</DocPanelCell>
                      <DocPanelCell header wide>{dpaSection.label}</DocPanelCell>
                    </div>
                    <div className="flex">
                      <DocPanelCell wide>{translate(ANM_I18N.classification.high)}</DocPanelCell>
                      <DocPanelCell wide>{translate(ANM_I18N.dpaPanel.bandHigh)}</DocPanelCell>
                    </div>
                    <div className="flex">
                      <DocPanelCell wide>{translate(ANM_I18N.classification.medium)}</DocPanelCell>
                      <DocPanelCell wide>{translate(ANM_I18N.dpaPanel.bandMedium)}</DocPanelCell>
                    </div>
                    <div className="flex">
                      <DocPanelCell wide>{translate(ANM_I18N.classification.low)}</DocPanelCell>
                      <DocPanelCell wide>{translate(ANM_I18N.dpaPanel.bandLow)}</DocPanelCell>
                    </div>
                  </div>
                </div>
                <div className={`border border-[#d9d9d9] bg-[#f2f2f2] px-2 py-1 text-xs font-medium text-[#4d4d4d] ${ctd.surfaceMuted} ${ctd.text}`}>
                  {translate(ANM_I18N.dpaPanel.matrixFooter)}
                </div>
              </div>
            </div>
          </DocPanelTable>
        ) : null}

        {renderGrades([dpaGrade], { dpa: dpaPartial })}
      </section>

      {/* GO */}
      <section className="flex flex-col gap-4">
        <p className="text-base font-normal text-foreground">{goSection.subtitle}</p>
        <h3 className="sr-only">{goSection.label}</h3>

        <SectionBignumber
          title={translate(ANM_I18N.label.goBignumberTitle)}
          total={goTotal}
          classificationLabel={goClassLabel}
          goLevel={goClassLevel}
          referenceDocAriaLabel={translate(ANM_I18N.label.referenceDocButton, { section: goSection.label })}
          totalAriaLabel={translate(ANM_I18N.label.total, { section: goSection.label })}
          classificationAriaLabel={translate(ANM_I18N.label.classificationStatus, {
            section: goSection.label,
          })}
          infoOpen={!!openPanels["go"]}
          onToggleInfo={() => togglePanel("go")}
        />

        {openPanels["go"] ? (
          <DocPanelTable
            panelAriaLabel={translate(ANM_I18N.label.referenceDocPanel, { section: goSection.label })}
            panelTitle={translate(ANM_I18N.label.referenceDocPanel, { section: goSection.label })}
            closeLabel={translate(ANM_I18N.label.close)}
            onClose={() => togglePanel("go")}
          >
            <div className="p-2">
              <div className={`rounded-xl border border-[#d9d9d9] bg-[#f2f2f2] px-2 py-1 text-center text-sm font-bold text-[#4d4d4d] ${ctd.surfaceMuted} ${ctd.text}`}>
                {translate(ANM_I18N.goPanel.tableTitle)}
              </div>

              <div className={`mt-2 overflow-hidden rounded-xl border border-[#d9d9d9] ${ctd.border}`}>
                <div className="flex">
                  <DocPanelCell header narrow>3</DocPanelCell>
                  <DocPanelCell header wide>{translate(ANM_I18N.goPanel.operationalCategory)}</DocPanelCell>
                  <DocPanelCell header>{translate(ANM_I18N.label.points)}</DocPanelCell>
                </div>
                <div className="flex">
                  <DocPanelCell header narrow>3.1</DocPanelCell>
                  <DocPanelCell wide align="center">{translate(ANM_I18N.goPanel.risrRow)}</DocPanelCell>
                  <DocPanelCell>{formatPanelPoints(selections["go-a"])}</DocPanelCell>
                </div>
                <div className="flex">
                  <DocPanelCell header narrow>3.2</DocPanelCell>
                  <DocPanelCell wide align="center">{translate(ANM_I18N.goPanel.rpsbRow)}</DocPanelCell>
                  <DocPanelCell>{formatPanelPoints(selections["go-b"])}</DocPanelCell>
                </div>
                <div className="flex">
                  <DocPanelCell header narrow>3.3</DocPanelCell>
                  <DocPanelCell wide align="center">{translate(ANM_I18N.goPanel.conformityRow)}</DocPanelCell>
                  <DocPanelCell>{formatPanelPoints(selections["go-c"])}</DocPanelCell>
                </div>
                <div className="flex">
                  <DocPanelCell header narrow>3.4</DocPanelCell>
                  <DocPanelCell wide align="center">{translate(ANM_I18N.goPanel.inspectionRow)}</DocPanelCell>
                  <DocPanelCell>{formatPanelPoints(selections["go-d"])}</DocPanelCell>
                </div>
                <div className="flex">
                  <DocPanelCell header narrow>3.5</DocPanelCell>
                  <DocPanelCell wide align="center">{translate(ANM_I18N.goPanel.edrRow)}</DocPanelCell>
                  <DocPanelCell>{formatPanelPoints(selections["go-e"])}</DocPanelCell>
                </div>
                <div className="flex">
                  <DocPanelCell header narrow>3.6</DocPanelCell>
                  <DocPanelCell wide align="center">{translate(ANM_I18N.goPanel.pgrbmRow)}</DocPanelCell>
                  <DocPanelCell>{formatPanelPoints(selections["go-f"])}</DocPanelCell>
                </div>
                <div className="flex">
                  <DocPanelCell header narrow>3.7</DocPanelCell>
                  <DocPanelCell wide align="center">{translate(ANM_I18N.goPanel.certificationRow)}</DocPanelCell>
                  <DocPanelCell>{formatPanelPoints(selections["go-g"])}</DocPanelCell>
                </div>
                <div className="flex">
                  <DocPanelCell wide align="right">
                    <span className="font-bold">{translate(ANM_I18N.goPanel.totalLabel)}</span>
                  </DocPanelCell>
                  <DocPanelCell>
                    <span className="font-bold">{formatPanelPoints(goTotal)}</span>
                  </DocPanelCell>
                </div>
                <div className="flex">
                  <DocPanelCell wide align="right">
                    <span className="font-bold">{translate(ANM_I18N.goPanel.operationalClassification)}</span>
                  </DocPanelCell>
                  <DocPanelCell>
                    <span className="font-bold">{goClassLabel}</span>
                  </DocPanelCell>
                </div>
              </div>

              <div className={`mt-2 overflow-hidden rounded-xl border border-[#d9d9d9] ${ctd.border}`}>
                <div className="flex">
                  <DocPanelCell header wide>{translate(ANM_I18N.goPanel.bandsTitle)}</DocPanelCell>
                  <div className="flex min-w-0 flex-1 flex-col">
                    <div className="flex">
                      <DocPanelCell header wide>{translate(ANM_I18N.goPanel.gopCategory)}</DocPanelCell>
                      <DocPanelCell header wide>{translate(ANM_I18N.label.points)}</DocPanelCell>
                    </div>
                    <div className="flex">
                      <DocPanelCell wide>AA</DocPanelCell>
                      <DocPanelCell wide>{translate(ANM_I18N.goPanel.bandAa)}</DocPanelCell>
                    </div>
                    <div className="flex">
                      <DocPanelCell wide>A</DocPanelCell>
                      <DocPanelCell wide>{translate(ANM_I18N.goPanel.bandA)}</DocPanelCell>
                    </div>
                    <div className="flex">
                      <DocPanelCell wide>B</DocPanelCell>
                      <DocPanelCell wide>{translate(ANM_I18N.goPanel.bandB)}</DocPanelCell>
                    </div>
                    <div className="flex">
                      <DocPanelCell wide>C</DocPanelCell>
                      <DocPanelCell wide>{translate(ANM_I18N.goPanel.bandC)}</DocPanelCell>
                    </div>
                    <div className="flex">
                      <DocPanelCell wide>D</DocPanelCell>
                      <DocPanelCell wide>{translate(ANM_I18N.goPanel.bandD)}</DocPanelCell>
                    </div>
                  </div>
                </div>
                <div className={`border border-[#d9d9d9] bg-[#f2f2f2] px-2 py-1 text-xs font-medium text-[#4d4d4d] ${ctd.surfaceMuted} ${ctd.text}`}>
                  {translate(ANM_I18N.goPanel.footnote)}
                </div>
              </div>
            </div>
          </DocPanelTable>
        ) : null}

        {renderGrades([goGrade], { go: goPartial })}
      </section>
    </form>
  );
};

export default AdminStructureAnmClassificationTab;
